import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
  Input,
  Table,
} from 'reactstrap';
import { toast } from 'react-toastify';

import { api } from '../../../config';
import { RIGHTS_FIELDS } from '../constants';
import {
  buildRightsToastMessage,
  getMenuName,
  hasRightsChanged,
  normalizeMenuRoleItem,
} from '../utils/menuRoleUtils';

const RightsModal = ({
  isOpen,
  toggle,
  token,
  selectedRole,
  menuOptions,
  onLoadingChange,
}) => {
  const [modalLoading, setModalLoading] = useState(false);
  const [rights, setRights] = useState([]);
  const [originalRights, setOriginalRights] = useState([]);
  const [searchText, setSearchText] = useState('');

  const resetState = () => {
    setRights([]);
    setOriginalRights([]);
    setSearchText('');
  };

  useEffect(() => {
    if (!isOpen || !selectedRole || !token) return;

    const loadRights = async () => {
      resetState();
      onLoadingChange?.(true);

      try {
        const response = await fetch(
          `${api.API_URL}/menu-role/by-role/${selectedRole.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error('Failed to load menu rights');

        const result = await response.json();
        const rawData = result.data?.items ?? result.data;
        const existingItems = Array.isArray(rawData) ? rawData : [];
        const normalized = existingItems
          .filter(item => item && item.menuId != null)
          .map(normalizeMenuRoleItem);

        setRights(normalized);
        setOriginalRights(normalized.map(item => ({ ...item })));
      } catch (error) {
        toast.error(`Error: ${error.message}`);
        toggle();
      } finally {
        onLoadingChange?.(false);
      }
    };

    loadRights();
  }, [isOpen, selectedRole, token]);

  const hasChanges = useMemo(() => {
    return rights.some(item => {
      const original = originalRights.find(o => o.id === item.id);
      return original && hasRightsChanged(item, original);
    });
  }, [rights, originalRights]);

  const filteredRights = useMemo(() => {
    const query = searchText.toLowerCase().trim();
    if (!query) return rights;

    return rights.filter(item => {
      const menuName = getMenuName(menuOptions, item.menuId).toLowerCase();
      return menuName.includes(query);
    });
  }, [rights, searchText, menuOptions]);

  const handleClose = () => {
    resetState();
    toggle();
  };

  const handleToggle = (mappingId, field) => {
    setRights(prev =>
      prev.map(item =>
        item.id === mappingId ? { ...item, [field]: !item[field] } : item
      )
    );
  };

  const handleToggleColumn = (field, value) => {
    const visibleIds = new Set(filteredRights.map(item => item.id));
    setRights(prev =>
      prev.map(item =>
        visibleIds.has(item.id) ? { ...item, [field]: value } : item
      )
    );
  };

  const isColumnChecked = field => {
    if (filteredRights.length === 0) return false;
    return filteredRights.every(item => item[field]);
  };

  const isColumnIndeterminate = field => {
    if (filteredRights.length === 0) return false;
    const checkedCount = filteredRights.filter(item => item[field]).length;
    return checkedCount > 0 && checkedCount < filteredRights.length;
  };

  const handleSave = async () => {
    if (!hasChanges) {
      toast.info('No changes to save');
      handleClose();
      return;
    }

    setModalLoading(true);
    try {
      const changedItems = rights.filter(item => {
        const original = originalRights.find(o => o.id === item.id);
        return original && hasRightsChanged(item, original);
      });

      const updatePromises = changedItems.map(item =>
        // fetch(`${api.API_URL}/menu-role/${item.id}`, {
        //   method: 'POST',
        //   headers: {
        //     'Content-Type': 'application/json',
        //     Authorization: `Bearer ${token}`,
        //   },
        //   body: JSON.stringify({
        //     roleId: selectedRole.id,
        //     menuId: item.menuId,
        //     canCreate: item.canCreate,
        //     canRead: item.canRead,
        //     canUpdate: item.canUpdate,
        //     canDelete: item.canDelete,
        //   }),
        // })
        fetch(`${api.API_URL}/menu-role/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            menuId: item.menuId,
            roles: [
              {
                roleId: selectedRole.id,
                canCreate: item.canCreate,
                canRead: item.canRead,
                canUpdate: item.canUpdate,
                canDelete: item.canDelete,
              },
            ],
          }),
        })
          .then(res => {
            if (!res.ok) {
              throw new Error(
                `Failed to update rights for ${getMenuName(menuOptions, item.menuId)}`
              );
            }
            return { menuId: item.menuId };
          })
          .catch(err => {
            throw { menuId: item.menuId, message: err.message };
          })
      );

      const results = await Promise.allSettled(updatePromises);
      const enrichedResults = results.map((res, idx) => ({
        ...res,
        menuId: changedItems[idx]?.menuId,
      }));

      const totalFailed = enrichedResults.filter(
        r => r.status === 'rejected'
      ).length;
      const message = buildRightsToastMessage(menuOptions, enrichedResults);

      if (totalFailed === 0) {
        toast.success(message);
        handleClose();
      } else {
        toast.warning(message, { autoClose: 6000 });

        const successIds = enrichedResults
          .filter(r => r.status === 'fulfilled')
          .map((_, idx) => changedItems[idx].id);

        setOriginalRights(prev =>
          prev.map(item => {
            if (!successIds.includes(item.id)) return item;
            const updated = rights.find(r => r.id === item.id);
            return updated ? { ...updated } : item;
          })
        );
      }
    } catch (error) {
      toast.error(`Save failed: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size='xl'
      backdrop='static'
      centered
    >
      <ModalHeader toggle={handleClose} className='bg-light'>
        Menu Rights for {selectedRole?.name || selectedRole?.id}
      </ModalHeader>
      <ModalBody>
        <div className='search-box mb-3'>
          <Input
            type='text'
            className='form-control'
            placeholder='Search assigned menu...'
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <i className='ri-search-line search-icon'></i>
        </div>

        {rights.length === 0 ? (
          <p className='text-muted text-center my-4'>
            No menus assigned to this role. Use Assign to add menus first.
          </p>
        ) : (
          <div className='table-responsive'>
            <Table bordered hover className='align-middle mb-0'>
              <thead className='table-light'>
                <tr>
                  <th>Menu</th>
                  {RIGHTS_FIELDS.map(field => (
                    <th
                      key={field.key}
                      className='text-center'
                      style={{ width: '100px' }}
                    >
                      <div className='d-flex flex-column align-items-center gap-1'>
                        <span>{field.label}</span>
                        <Input
                          type='checkbox'
                          className='form-check-input'
                          checked={isColumnChecked(field.key)}
                          ref={el => {
                            if (el) {
                              el.indeterminate = isColumnIndeterminate(
                                field.key
                              );
                            }
                          }}
                          onChange={e =>
                            handleToggleColumn(field.key, e.target.checked)
                          }
                          disabled={filteredRights.length === 0}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRights.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='text-center text-muted py-4'>
                      No menus match your search.
                    </td>
                  </tr>
                ) : (
                  filteredRights.map(item => (
                    <tr key={item.id}>
                      <td>{getMenuName(menuOptions, item.menuId)}</td>
                      {RIGHTS_FIELDS.map(field => (
                        <td key={field.key} className='text-center'>
                          <Input
                            type='checkbox'
                            className='form-check-input'
                            checked={item[field.key]}
                            onChange={() => handleToggle(item.id, field.key)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color='light' onClick={handleClose} disabled={modalLoading}>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleSave}
          disabled={modalLoading || !hasChanges || rights.length === 0}
        >
          {modalLoading ? (
            <Spinner size='sm' className='me-2' />
          ) : (
            <i className='ri-save-line me-1'></i>
          )}
          Save Rights
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RightsModal;
